import { z } from 'zod';
import { SsoOpenIdConnectionResult, useRepositoryMutation } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { createExperimentalZodFormResolver, LocalizedText, UseFormControlsParams } from '@mews/framework';
import {
    ConfigureSsoPanelDomains,
    ConfigureSsoPanelFormData,
    parseFormUserDomains,
    parseSsoConfigToForm,
    ssoDomainsSchema,
} from '../../ConfigureSsoPanel';
import { SsoEditPanel } from './SsoEditPanel';

export interface SsoEditUserDomainsProps {
    data: SsoOpenIdConnectionResult;
    active: boolean;
    onClose: () => void;
}

export function SsoEditUserDomains({ data, ...dialogProps }: SsoEditUserDomainsProps) {
    const { onSubmit } = useEditUserDomainsForm();

    return (
        <SsoEditPanel
            title={<LocalizedText textKey={TextKey.SsoDetailsManageUserDomainsTitle} />}
            id="edit-sso-user-domains"
            defaultValues={parseSsoConfigToForm(data)}
            resolver={createExperimentalZodFormResolver(editUserDomainsSchema)}
            onSubmit={onSubmit}
            {...dialogProps}
        >
            <ConfigureSsoPanelDomains />
        </SsoEditPanel>
    );
}

const editUserDomainsSchema = z.object(ssoDomainsSchema);

type EditSsoUserDomainsFormData = Pick<ConfigureSsoPanelFormData, 'domains' | 'enforcedDomains'>;

export function useEditUserDomainsForm(): UseFormControlsParams<EditSsoUserDomainsFormData> {
    const { mutateAsync } = useRepositoryMutation('ssoConnection', 'updateDomains', {
        invalidateQueries: { ssoConnection: ['getOpenId'], securityOverview: ['getOverview'] },
        meta: {
            disableDefaultErrorHandler: true,
        },
    });

    return {
        onSubmit: async ({ domains, enforcedDomains }: EditSsoUserDomainsFormData) => {
            await mutateAsync({ domains: parseFormUserDomains({ domains, enforcedDomains }) });
        },
    };
}
